// ============================================================================
// VIEW CONTEXT (multi-access)
// "Viewing as: Owner | Cleaner" — persisted; Owner when user owns or co-hosts properties.
// ============================================================================

import { createContext, useContext, useCallback, useState, useEffect, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import { lockedWorkspace } from "@/lib/workspaceMode";

export type ViewContextType = "owner" | "cleaner";

const STORAGE_KEY = "view_context";

function getStored(): ViewContextType | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "owner" || v === "cleaner") return v;
    // Migrate legacy team_admin → owner portal
    if (v === "team_admin") return "owner";
  } catch {}
  return null;
}

function defaultContext(is_owner: boolean, is_cohost: boolean, is_cleaner: boolean): ViewContextType {
  if (is_owner || is_cohost) return "owner";
  if (is_cleaner) return "cleaner";
  return "owner";
}

interface ViewContextValue {
  viewContext: ViewContextType;
  setViewContext: (ctx: ViewContextType) => void;
  available: ViewContextType[];
}

const ViewContext = createContext<ViewContextValue | null>(null);

export function ViewContextProvider({ children }: { children: ReactNode }) {
  const { is_owner, is_cohost, is_cleaner, has_cleaner_assignment, isLoading } = useUserCapabilities();
  const location = useLocation();

  const available: ViewContextType[] = [];
  if (is_owner || is_cohost) available.push("owner");
  if (is_cleaner) available.push("cleaner");
  if (available.length === 0) available.push("owner");

  const defaultVal = defaultContext(is_owner, is_cohost, is_cleaner);

  const [viewContext, setState] = useState<ViewContextType>(() => getStored() ?? defaultVal);

  useEffect(() => {
    const stored = getStored();
    const nextDefault = defaultContext(is_owner, is_cohost, is_cleaner);
    if (!isLoading) {
      const locked = lockedWorkspace({ is_owner, is_cohost, is_cleaner, has_cleaner_assignment });
      const canUseStored =
        locked != null
          ? false
          : stored === "owner"
            ? is_owner || is_cohost || !is_cleaner
            : stored === "cleaner"
              ? is_cleaner
              : false;
      const valid = locked ?? (canUseStored ? stored! : nextDefault);
      setState(valid);
      try {
        localStorage.setItem(STORAGE_KEY, valid);
      } catch {}
    }
  }, [isLoading, is_owner, is_cohost, is_cleaner, has_cleaner_assignment]);

  useEffect(() => {
    const locked = lockedWorkspace({ is_owner, is_cohost, is_cleaner, has_cleaner_assignment });
    if (locked) return;
    const path = location.pathname;
    if (
      path.startsWith("/owner/properties") ||
      path.startsWith("/properties") ||
      path.startsWith("/owner/")
    ) {
      setState("owner");
      try {
        localStorage.setItem(STORAGE_KEY, "owner");
      } catch {}
      return;
    }
    if (
      path.startsWith("/worker") ||
      path.startsWith("/cleaner") ||
      path.startsWith("/clean/")
    ) {
      if (is_cleaner) {
        setState("cleaner");
        try {
          localStorage.setItem(STORAGE_KEY, "cleaner");
        } catch {}
      }
    }
  }, [location.pathname, is_cleaner, is_owner, is_cohost, has_cleaner_assignment]);

  const setViewContext = useCallback((ctx: ViewContextType) => {
    setState(ctx);
    try {
      localStorage.setItem(STORAGE_KEY, ctx);
    } catch {}
  }, []);

  const value: ViewContextValue = {
    viewContext: available.includes(viewContext) ? viewContext : available[0] ?? "owner",
    setViewContext,
    available,
  };

  return <ViewContext.Provider value={value}>{children}</ViewContext.Provider>;
}

export function useViewContext(): ViewContextValue {
  const ctx = useContext(ViewContext);
  if (!ctx) {
    return {
      viewContext: "owner",
      setViewContext: () => {},
      available: ["owner"],
    };
  }
  return ctx;
}

/** Set view context from invite flow without full provider mount. */
export function setViewContextFromInvite(context: ViewContextType) {
  try {
    localStorage.setItem(STORAGE_KEY, context);
  } catch {}
}
